import './Draw.css'
import React, { useState, useRef, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { useSetState, useKeyPressEvent, useEvent, usePrevious } from 'react-use'
import Shape from '../annotations/Shape'
import Area from './DrawArea'
import StrikeoutLine from '../annotations/StrikeoutLine'
import HighlightRect from '../annotations/HighlightRect'
import Point from '../annotations/Point'
import { getRectFromCoords } from '../annotations/utils'
import uuid from 'uuid'
import {
  getPointFromEvent,
  getPosition,
  createAreaAnnotation,
  createInkAnnotation,
  createPointAnnotation,
  createFreetextAnnotation,
  createStrikeoutAnnotation,
  createHighlightAnnotation,
  resetTouchSmoothing,
} from './utils'
import { isPenEvent, isTouchEvent } from '../utils'
import {
  DEFAULT_INK_ANNOTATION_COLOR,
  DEFAULT_STRIKEOUT_ANNOTATION_COLOR,
  DEFAULT_AREA_ANNOTATION_COLOR,
  DEFAULT_HIGHLIGHT_ANNOTATION_COLOR,
} from '../constants'
import classnames from 'classnames'

const ALL_ANNOTATION_TYPES = ['area', 'freetext', 'highlight', 'ink', 'point', 'strikeout']
const TEXT_SELECTION_ANNOTATION_TYPES = ['highlight', 'strikeout']

const NON_PROP_DEFAULT_STATE = {
  paths: [],
}
class Draw_ extends React.Component {
  componentDidMount = () => {
    const div = this.props.div.current
    if (div instanceof HTMLElement === false) {
      return
    }
    div.addEventListener('pointerdown', this.handlePointerDown)
  }

  componentWillUnmount = () => {
    if (this.props.div.current instanceof HTMLElement === false) {
      return
    }
    const div = this.props.div.current
    div.removeEventListener('pointerdown', this.handlePointerDown)
  }

  handleMouseDown = (event) => {
    if (window.PointerEvent) return
    event.stopPropagation()
    this.handlePointerDown(event)
  }

  handlePointerDown = (event) => {
    const isTouch = isTouchEvent(event)

    if (isPenEvent(event) && this.props.type !== 'selection') {
      this.props.onIsPenAnnotatingChange(true)
    }

    if (
      this.props.isDrawing ||
      ALL_ANNOTATION_TYPES.includes(this.props.type) === false ||
      this.props.isHoveringOverAnnotation ||
      (event.target instanceof HTMLElement &&
        event.target.closest('.AnnotationToolbar') instanceof HTMLElement)
    ) {
      this.props.setIsPointerDown(true)
      return false
    }

    // If we reach here, drawing is intended.
    // Prevent default browser actions (like scrolling) for touch events.
    if (isTouch) {
      event.preventDefault()
      resetTouchSmoothing() // Reset smoothing state for new touch interaction
    }

    if (TEXT_SELECTION_ANNOTATION_TYPES.includes(this.props.type)) {
      this.props.ensureTextLayerDivRects()
    }
    if (this.props.div.current instanceof HTMLElement === false) {
      throw new Error('div missing in <Draw>')
    }

    const point = getPointFromEvent(
      this.props.div.current,
      event,
      this.props.type,
      this.props.strokeWidth,
    )
    // TODO: implement opacity
    // let opacity = pressure >= 0.5 ? 1 : Math.sqrt(0.5 + pressure)
    const isOutOfBounds =
      point.x < 0 ||
      point.x > this.props.viewportWidth ||
      point.y < 0 ||
      point.y > this.props.viewportHeight
    if (isOutOfBounds) {
      console.warn('point out of bounds')
      return false
    }
    const temporaryAnnotationId =
      typeof this.props.multiStrokeAnnotationId === 'string' && this.props.drawings.length > 0
        ? this.props.temporaryAnnotationId
        : uuid.v4()
    this.props.onDrawingStart()
    this.props.setIsDrawing(true)
    this.props.setIsPointerDown(true)
    this.props.setTemporaryAnnotationId(temporaryAnnotationId)
    this.props.setState({
      paths: [[point]],
    })
    if (this.props.type === 'ink') {
      this.props.onMultiStrokeAnnotation(temporaryAnnotationId)
    }
  }

  handleMouseOut = (_event) => {
    if (window.Pointer) {
      return
    }
    this.props.handlePointerLeave()
  }

  render() {
    let paths = []

    if (this.props.type === 'point' && this.props.state.paths.length > 0) {
      const annotationObject = createPointAnnotation(
        uuid.v4(),
        this.props.state.paths,
        this.props.viewportScale,
        this.props.currentColor,
      )
      if (annotationObject !== null && annotationObject.rect instanceof Array) {
        const [[x1, y1], [x2, y2]] = annotationObject.rect
        const width = x2 - x1
        const height = y1 - y2
        paths = [
          <g key="point" role="presentation" width={width} height={height}>
            <Point
              x={x1 + 2}
              y={y2 + 2}
              width={width}
              height={height}
              color={
                typeof this.props.currentColor === 'string'
                  ? this.props.currentColor
                  : annotationObject.color
              }
            />
          </g>,
        ]
      }
    }

    if (this.props.type === 'ink' && this.props.state.paths.length > 0) {
      // currently being drawn
      paths = this.props.state.paths.map((points, index) => {
        return (
          <Shape
            key={`${this.props.temporaryAnnotationId}-${index}`}
            strokeColor={
              typeof this.props.currentColor === 'string'
                ? this.props.currentColor
                : DEFAULT_INK_ANNOTATION_COLOR
            }
            strokeWidth={this.props.strokeWidth}
            points={points}
          />
        )
      })
    }

    if (this.props.type === 'area') {
      paths = this.props.state.paths.map((path, index) => {
        return (
          <Area
            key={`${this.props.temporaryAnnotationId}-${index}`}
            strokeColor={
              typeof this.props.currentColor === 'string'
                ? this.props.currentColor
                : DEFAULT_AREA_ANNOTATION_COLOR
            }
            strokeWidth={this.props.strokeWidth}
            path={path}
          />
        )
      })
    }

    if (this.props.type === 'strikeout' && this.props.textLayerDivRects.current.length > 0) {
      const annotationObject = createStrikeoutAnnotation(
        uuid.v4(),
        this.props.viewportHeight,
        this.props.viewportWidth,
        this.props.viewportScale,
        this.props.currentColor,
        getPosition(this.props.div.current),
        this.props.textLayerDivRects.current,
      )
      if (annotationObject !== null) {
        paths = annotationObject.coords.map((rect, index) => {
          return (
            <g
              className="Draw--strikeout-group"
              key={`${this.props.temporaryAnnotationId}-${index}`}
              role="presentation"
              fillOpacity={1}
              stroke={
                typeof this.props.currentColor === 'string'
                  ? this.props.currentColor
                  : DEFAULT_STRIKEOUT_ANNOTATION_COLOR
              }
              strokeWidth="1.5"
            >
              <StrikeoutLine
                strokeColor={this.props.currentColor}
                strokeWidth={this.props.strokeWidth}
                rect={rect}
              />
            </g>
          )
        })
      }
    }

    if (this.props.type === 'highlight' && this.props.textLayerDivRects.current.length > 0) {
      const annotationObject = createHighlightAnnotation(
        uuid.v4(),
        this.props.viewportHeight,
        this.props.viewportWidth,
        this.props.viewportScale,
        this.props.currentColor,
        getPosition(this.props.div.current),
        this.props.textLayerDivRects.current,
      )

      if (annotationObject !== null) {
        paths = annotationObject.coords.map(getRectFromCoords).map((rect, index) => {
          return (
            <g
              className="Draw--highlight-group"
              key={index}
              role="presentation"
              fill={
                typeof this.props.currentColor === 'string'
                  ? this.props.currentColor
                  : DEFAULT_HIGHLIGHT_ANNOTATION_COLOR
              }
            >
              <HighlightRect rect={rect} />
            </g>
          )
        })
      }
    }

    const drawClassnames = classnames('Draw', {
      'Draw--not-drawing': this.props.isDrawing === false,
      'Draw--is-drawing': this.props.isDrawing,
      'Draw--is-pointer-down': this.props.isPointerDown,
      'Draw--selection': this.props.type === 'selection',
      'Draw--freetext': this.props.type === 'freetext',
      'Draw--highlight': this.props.type === 'highlight',
      'Draw--strikeout': this.props.type === 'strikeout',
      'Draw--area': this.props.type === 'area',
      'Draw--ink': this.props.type === 'ink',
      'Draw--point': this.props.type === 'point',
    })

    const divStyle = {
      ...(typeof this.props.style === 'object' && this.props.style !== null
        ? this.props.style
        : {}),
      touchAction: 'none',
    }

    /*
    Firefox overscrolls .Pages in between drawing ink annotation strokes
      if .Draw--svg-container is removed.
      ¯\_(ツ)_/¯
    */
    const shoulShowDrawSvg = this.props.type === 'ink' || paths.length > 0
    let x, y, children
    if (paths.length > 0) {
      children = paths[0].props.children
      if (children) {
        x = children.props.x
        y = children.props.y
      }
    }
    return (
      <div
        className={drawClassnames}
        onMouseDown={this.handleMouseDown}
        onMouseMove={(event) => this.props.handleMouseMove(event)}
        onMouseOut={this.handleMouseOut}
        onTouchEnd={(event) => {
          if (window.PointerEvent) return // Avoid duplicate events when PointerEvent is supported
          const { isDrawing, handlePointerUp } = this.props
          if (isTouchEvent(event) && isDrawing) {
            // If a drawing operation was in progress with touch, prevent default on touch end.
            event.preventDefault()
          }
          handlePointerUp(event) // Pass to the main handler in Draw functional component
        }}
        onTouchMove={(event) => {
          if (window.PointerEvent) return // Avoid duplicate events when PointerEvent is supported
          this.props.handlePointerMove(event)
        }}
        onTouchStart={(event) => {
          if (window.PointerEvent) return // Avoid duplicate events when PointerEvent is supported
          this.handlePointerDown(event)
        }}
        ref={this.props.div}
        style={divStyle}
      >
        {shoulShowDrawSvg && (
          <div className="Draw--svg-container">
            <svg
              className="Draw-svg"
              style={{
                width: `${this.props.width}px`,
                height: `${this.props.height}px`,
                position: 'absolute',
                left: 0,
                top: 0,
              }}
            >
              {paths}
            </svg>
            {x && y && (
              <div
                role="text"
                aria-label={`Coordinates are x, ${Math.floor(x)} and y, ${Math.floor(y)}`}
                className="Carrot-range"
                style={{
                  width: `${this.props.width}px`,
                  height: `${this.props.height}px`,
                  left: `${x}px`,
                  top: `${y}px`,
                }}
              >
                {`(${Math.floor(x)}, ${Math.floor(y)})`}
              </div>
            )}
          </div>
        )}
        {this.props.children}
      </div>
    )
  }
}

const areEqual = (prevProps, nextProps) => {
  return !nextProps.shouldBeRendered
}

function Draw(props) {
  const { type } = props
  const div = useRef(null)
  // isPenActive needs to be a class member variable and not a state variable
  // because we need to be able to assign it a value and have that value available
  // immediately. (due to touch events coming in at the same time as pointer events)
  const isPenActive = useRef(false)
  const textLayerDivRects = useRef([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [isPointerDown, setIsPointerDown] = useState(false)
  const [drawings, setDrawings] = useState([])
  const [temporaryAnnotationId, setTemporaryAnnotationId] = useState('')
  const [state, setState] = useSetState({ paths: [] })
  const [lastForceUpdate, setLastForceUpdate] = useSetState(null)
  const prevType = usePrevious(type)

  const handleContextMenu = (event) => {
    const isPenEvent = event.buttons === 0
    if (isPenEvent && props.shouldBlockContextMenu) {
      event.preventDefault()
      if (type === 'selection') {
        props.onBlockContextMenu(false)
      }
    }
  }
  useEvent('contextmenu', handleContextMenu, div.current)

  const handleCreateAnnotation = () => {
    if (div.current instanceof HTMLElement === false) {
      return
    }

    const isInkDrawing = props.type === 'ink' // TODO: make unnecessary
    const paths = state.paths
    const color = props.currentColor
    const bgColor = props.currentBgColor
    const font = props.currentFont
    const strokeWidth = props.strokeWidth
    const viewportHeight = props.viewportHeight
    const viewportWidth = props.viewportWidth
    const viewportScale = props.viewportScale
    const divPosition = getPosition(div.current)

    let annotationObject
    switch (props.type) {
      case 'ink':
        if (drawings == null || drawings.length === 0) {
          console.error('drawings cannot be empty when creating ink annotation', { drawings })
          return
        }
        // Use temporaryAnnotationId if we're in multi-stroke mode, otherwise create new ID
        const annotationId = temporaryAnnotationId || uuid.v4()
        annotationObject = createInkAnnotation(annotationId, drawings, color, strokeWidth)
        break
      case 'point':
        if (paths == null || paths.length === 0) {
          console.error('paths cannot be empty when creating point annotation', { paths })
          return
        }
        annotationObject = createPointAnnotation(uuid.v4(), paths, viewportScale, color)
        break
      case 'area':
        annotationObject = createAreaAnnotation(uuid.v4(), paths, color, strokeWidth)
        break
      case 'strikeout':
        annotationObject = createStrikeoutAnnotation(
          uuid.v4(),
          viewportHeight,
          viewportWidth,
          viewportScale,
          color,
          divPosition,
          textLayerDivRects.current,
        )
        break
      case 'highlight':
        annotationObject = createHighlightAnnotation(
          uuid.v4(),
          viewportHeight,
          viewportWidth,
          viewportScale,
          color,
          divPosition,
          textLayerDivRects.current,
        )
        break
      case 'freetext':
        annotationObject = createFreetextAnnotation({
          id: uuid.v4(),
          paths,
          viewportHeight,
          viewportWidth,
          viewportScale,
          color,
          bgColor,
          font,
          strokeWidth,
          divPosition,
          textLayerDivRects: textLayerDivRects.current,
        })
        break
      default:
        throw new Error('invalid annotation type')
    }

    if (annotationObject != null) {
      props.onChange({
        ...annotationObject,
        // Don't override the ID - use the one from annotationObject
      })
      props.onDrawingEnd()
      if (isInkDrawing && !props.multiStrokeAnnotationId) {
        // Only clear if we're not in multi-stroke mode
        setDrawings([]) // Clear drawings for the current ink annotation as it's now submitted
        setTemporaryAnnotationId('') // Also clear the temporary ID
      }
    }
    setTimeout(() => {
      if (isInkDrawing === false) {
        setIsDrawing(false)
        setIsPointerDown(false)
        setDrawings([])
        setTemporaryAnnotationId('')
        setState(NON_PROP_DEFAULT_STATE)
      }
    }, 0)
  }

  const addStrokeToInkAnnotation = () => {
    const hasSufficientPath = state.paths.length === 1 && state.paths[0].length >= 1
    if (hasSufficientPath) {
      setIsDrawing(false)
      setDrawings([...drawings, state.paths[0]])
      setState({
        paths: [],
      })
    }
  }

  useEffect(() => {
    if (drawings.length > 0) {
      handleCreateAnnotation()
    }
  }, [drawings])

  const ensureTextLayerDivRects = () => {
    if (div.current instanceof HTMLElement === false) {
      return
    }
    const textDivs = div.current.querySelectorAll('.textLayer > span')
    textLayerDivRects.current = [...textDivs].map((div) => {
      const rect = div.getBoundingClientRect()
      // Needed because of PDF.js enhanced text selection modifications
      const normalizedRect = {
        left: rect.left,
        top: rect.top + parseFloat(div.style.paddingTop || 0),
        right: rect.right,
        bottom: rect.bottom - parseFloat(div.style.paddingBottom || 0),
      }
      return normalizedRect
    })
  }

  const handleSelectionChange = () => {
    const selection = window.getSelection()
    if (
      selection instanceof Selection &&
      selection.rangeCount > 0 &&
      selection.isCollapsed === false
    ) {
      const range = selection.getRangeAt(0)
      if (
        selection.rangeCount &&
        div.current instanceof HTMLElement &&
        div.current.contains(range.commonAncestorContainer)
      ) {
        setLastForceUpdate(Date.now())
      }
    }
  }

  const handleKeyDown = (_event) => {
    if (isDrawing) {
      props.onMultiStrokeAnnotation(null)
      props.onDrawingEnd()
      textLayerDivRects.current = []
      setIsDrawing(false)
      setIsPointerDown(false)
      setDrawings([])
      setTemporaryAnnotationId('')
      setState(NON_PROP_DEFAULT_STATE)
    }
  }
  useKeyPressEvent('Escape', handleKeyDown)

  const handlePointerUpCaptured = () => {
    if (props.type === 'ink' && state.paths && state.paths.length) {
      addStrokeToInkAnnotation()
      return
    }
  }

  const handlePointerUp = (event) => {
    if (isPenEvent(event)) {
      isPenActive.current = false
    }
    if (isDrawing === false) {
      setIsPointerDown(false)
    }
    if (props.type === 'ink') {
      return
    }
    if (
      ALL_ANNOTATION_TYPES.includes(props.type) === false ||
      event.target.closest('.Annotation') instanceof HTMLElement
    ) {
      return false
    }
    const pagesContainer = document.querySelector('.Pages')
    if (props.type !== 'ink' && pagesContainer.contains(event.target)) {
      const eventWasManuallyDispatched = event.screenX === 0 && event.screenY === 0
      if (eventWasManuallyDispatched || (state.paths && state.paths.length)) {
        flushSync(() => {
          handleCreateAnnotation()
        })
      }
    }
    props.onDrawingEnd()
    setIsDrawing(false)
  }

  const handlePointerMove = (event) => {
    if (['selection', 'highlight', 'strikeout'].includes(type)) {
      return false
    }
    const isNotSelection = type !== 'selection'
    if (isNotSelection && isPenActive.current === false && isPenEvent(event)) {
      isPenActive.current = true
      props.onIsPenAnnotatingChange(true)
    }
    if (type === 'point') {
      if (div.current instanceof HTMLElement === false) {
        console.warn('<div> unexpectedly missing in <Draw>')
        return
      }
      event.preventDefault()
      const point = getPointFromEvent(div.current, event, type, props.strokeWidth)
      setState({
        paths: [[point]],
      })
      return
    }
    if (isDrawing === false) {
      return false
    }
    event.preventDefault()
    // TODO: re-enable coalesced events when performant. requestAnimationFrame?
    /*
    const useCoalescedEvents =
      false && event.getCoalescedEvents && event.getCoalescedEvents().length
    if (useCoalescedEvents) {
      for (const event of event.getCoalescedEvents()) {
        handlePointerMove(event)
      }
      return false
    }
    */

    const point = getPointFromEvent(div.current, event, type, props.strokeWidth)

    // TODO: implement opacity
    // let opacity = pressure >= 0.5 ? 1 : Math.sqrt(0.5 + pressure)
    setState({
      paths: [((state.paths && state.paths[0]) || []).concat([point])],
    })
  }
  useEvent('pointermove', handlePointerMove, div.current)
  useEvent('pointerup', handlePointerUp, document)
  useEvent('pointerup', handlePointerUpCaptured, document, { capture: true })
  useEvent('selectionchange', handleSelectionChange, document)

  const handleMouseUp = (event) => {
    if (ALL_ANNOTATION_TYPES.includes(type) === false) {
      setIsPointerDown(false)
    }
    if (window.PointerEvent) {
      return false
    } else {
      handlePointerUp(event)
    }
  }
  useEvent('mouseup', handleMouseUp, document)

  const handleMouseMove = (event) => {
    if (window.PointerEvent) return false
    handlePointerMove(event)
  }

  useEffect(() => {
    if (isDrawing && props.multiStrokeAnnotationId === null) {
      setIsDrawing(false)
      setIsPointerDown(false)
      setDrawings([])
      setTemporaryAnnotationId('')
      setState(NON_PROP_DEFAULT_STATE)
    }
    if (props.multiStrokeAnnotationId === null && drawings.length > 0) {
      setDrawings([])
    }
  }, [props.multiStrokeAnnotationId])

  // Warn users if they try to leave with an unsaved multi-stroke annotation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only warn if there's an active multi-stroke session with drawings
      if (props.multiStrokeAnnotationId && drawings.length > 0) {
        e.preventDefault()
        e.returnValue = true
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [props.multiStrokeAnnotationId, drawings.length])

  const handleScroll = () => {
    if (['highlight', 'strikeout'].includes(type) === false) {
      return false
    }
    ensureTextLayerDivRects()
    handleSelectionChange()
  }
  useEffect(() => {
    const pagesContainer = document.querySelector('.Pages')
    pagesContainer.addEventListener('scroll', handleScroll)
    return () => {
      pagesContainer.removeEventListener('scroll', handleScroll)
    }
  })

  useEffect(() => {
    if (prevType === 'selection' && TEXT_SELECTION_ANNOTATION_TYPES.includes(type)) {
      const selection = window.getSelection()
      if (
        selection instanceof Selection &&
        selection.rangeCount &&
        selection.isCollapsed === false
      ) {
        const range = selection.getRangeAt(0)

        // We check parentNode too since IE11 fails "contains" check if
        //   commonAncestorContainer is a text node
        const isSelectionWithinDiv =
          div.current.contains(range.commonAncestorContainer) ||
          div.current.contains(range.commonAncestorContainer.parentNode)

        const shouldCreateNewAnnotation = div.current instanceof HTMLElement && isSelectionWithinDiv

        if (shouldCreateNewAnnotation) {
          ensureTextLayerDivRects()
          handleCreateAnnotation()
        }
      }
    }

    // Right clicking w/ pen is only available in "selection" mode.

    const annotationTypeSwitchedToSelection = prevType !== 'selection' && type === 'selection'
    if (annotationTypeSwitchedToSelection) {
      props.onBlockContextMenu(false)
    }

    const annotationTypeSwitchedFromSelection = prevType === 'selection' && type !== 'selection'
    if (annotationTypeSwitchedFromSelection) {
      props.onBlockContextMenu(true)
    }
  }, [type])

  const handlePointerLeave = (event) => {
    isPenActive.current = false
    if (isPenEvent(event)) {
      props.onIsPenAnnotatingChange(false)
    }
    if (type === 'point') {
      setIsDrawing(false)
      setIsPointerDown(false)
      setDrawings([])
      setTemporaryAnnotationId('')
      setState(NON_PROP_DEFAULT_STATE)
    } else if (TEXT_SELECTION_ANNOTATION_TYPES.includes(type)) {
      setLastForceUpdate(Date.now())
      // Repeat next tick because selection might not be updated
      setTimeout(() => {
        setLastForceUpdate(Date.now())
      }, 0)
    }
  }

  useEvent('pointercancel', handlePointerLeave, document)
  useEvent('pointerleave', handlePointerLeave, div.current)

  return (
    <Draw_
      {...props}
      isDrawing={isDrawing}
      setIsDrawing={setIsDrawing}
      isPointerDown={isPointerDown}
      setIsPointerDown={setIsPointerDown}
      drawings={drawings}
      textLayerDivRects={textLayerDivRects}
      isPenActive={isPenActive}
      temporaryAnnotationId={temporaryAnnotationId}
      setTemporaryAnnotationId={setTemporaryAnnotationId}
      div={div}
      state={state}
      setState={setState}
      handleCreateAnnotation={handleCreateAnnotation}
      addStrokeToInkAnnotation={addStrokeToInkAnnotation}
      ensureTextLayerDivRects={ensureTextLayerDivRects}
      lastForceUpdate={lastForceUpdate}
      setLastForceUpdate={setLastForceUpdate}
      handleSelectionChange={handleSelectionChange}
      handlePointerUp={handlePointerUp}
      handlePointerMove={handlePointerMove}
      handleMouseUp={handleMouseUp}
      handleMouseMove={handleMouseMove}
      handlePointerLeave={handlePointerLeave}
    />
  )
}

export default React.memo(Draw, areEqual)
